import { Anchor, List, Table, Text } from '@mantine/core'

import { Section } from '../../components/SectionHeader'
import { LegalPage } from './LegalPage'

const RETENTION: Array<[string, string]> = [
  ['Email address', 'Until your account is purged'],
  ['Sign-in codes', '10 minutes'],
  ['Sessions', 'Up to 90 days'],
  ['IP address (rate limiting)', 'Held in memory only, never written to storage'],
  ['AI conversation history', 'Until you delete it'],
  ['AI reports', '1 year, or until your account is deleted'],
  ['AI rate-limit counters', '3 minutes (per-minute count), 2 hours (throttle-hit count)'],
  ["Daily AI allowance and rewarded-ad credits (with each viewing's unique id)", 'About a day (25 hours)'],
  ['AI strike history', "Strikes older than 90 days stop counting; the record itself isn't separately deleted"],
  ['Restrictions on an account (limits, pauses)', 'While they apply, then up to 1 year'],
  [
    'Blocklisted email (suspended or banned accounts)',
    '12 months, as a one-way hash plus a masked form (for example jo•••@gmail.com) for staff, to stop re-registration',
  ],
  [
    'Content-hold evidence',
    'Released: deleted at once. Suspended or banned without a report: 30 days after the decision. Reported: until the authorities say it can go',
  ],
  ['Usage counts and abnormal-usage flags', '13 months'],
  [
    'Push token or browser subscription',
    'Until you turn notifications off, sign out or delete your account; dropped after 270 days unseen',
  ],
  ['Support emails', '2 years'],
  ['Staff audit log', '2 years'],
  ['Weekly reviews', 'Until you delete it or your account'],
  ['Imported files', 'Until purged'],
  ['Server logs', '3 days'],
  ['Export files', '24 hours'],
  ['Deleted photos', 'Removed from storage once deleted'],
  ['Account deletion', '30-day grace period, then a further 30 days in backups'],
]

/**
 * `/privacy`: no sign-in needed. The source text is also kept in docs/legal/privacy.md for the owner to review in
 * git; keep the two in sync.
 */
export function PrivacyPage() {
  return (
    <LegalPage title="Privacy policy">
      <Text size="sm">
        Macrotide is a personal nutrition and training app from Ikaros Dev. This page explains what we collect, why,
        and what you can do about it. It&apos;s offered worldwide.
      </Text>
      <Text size="sm">
        <b>Controller:</b> [Owner&apos;s full legal name], trading as Ikaros Dev, [postal address]. Contact:
        macrotide@ikarosdev.com. <b>ICO registration:</b> [ICO registration number].
      </Text>
      <Text size="sm">
        <b>EU representative:</b> [To be decided by the owner].
      </Text>
      <Text size="sm">
        <b>EU legal representative (Digital Services Act):</b> [To be decided by the owner].
      </Text>
      <Text size="sm">
        <b>Point of contact for users and authorities:</b> macrotide@ikarosdev.com, in English.
      </Text>

      <Section title="Guest mode and accounts" card={false}>
        <Text size="sm">
          Android works without an account, except AI features (&quot;Sign in to use AI&quot;). The web app needs an
          account to sign in at all. In guest mode, your data stays on your device and nothing you log is sent to us.
          Food searches and database downloads still reach our server, carrying your IP address so the response can
          be sent back; we don&apos;t store that IP. If you create an account, your phone uploads everything you
          already have so your devices match, and from then on your data syncs to our servers on AWS, in the
          eu-west-2 (London) region.
        </Text>
        <Text size="sm" mt="xs">
          If you sign into an account that already has data, you choose keep, combine or overwrite (overwrite asks
          you to confirm a second time).
        </Text>
      </Section>

      <Section title="Health data consent" card={false}>
        <Text size="sm">
          Before Macrotide stores your health information on our servers, we ask you to agree to this, separately and
          explicitly, after you sign in: &quot;Store my health information on Macrotide&apos;s servers. Macrotide
          stores your food log, weight and body measurements, progress photos, workouts and anything imported from
          Health Connect on its servers, to sync your devices and run your coaching. You can withdraw any time by
          deleting your account.&quot; Declining signs you out.
        </Text>
      </Section>

      <Section title="What we collect" card={false}>
        <Text size="sm">
          If you choose to enter it, Macrotide stores: your food log, recipes and custom foods, weight, body metrics
          including waist, your profile (sex, birth date, height, activity level, and optionally your name, body fat %
          and ethnic background), goals, workouts, training programs, equipment, water, habits, and photos (progress
          photos and food photos you send to AI).
        </Text>
        <Text size="sm" mt="xs">
          To run your account we also keep: your email address and account id, your sign-in sessions, counts of how
          you use features (see &quot;Usage counts&quot;), your notification settings and device push token if you
          turn notifications on, emails you send us, and any restrictions on your account (strikes, holds,
          suspensions).
        </Text>
      </Section>

      <Section title="Health Connect (Android)" card={false}>
        <Text size="sm">
          With your permission, Macrotide reads weight, body fat, steps, active calories, sleep, heart rate
          variability, resting heart rate and exercise sessions from Health Connect (exercise sessions are read live
          and not stored separately), and imports in the background every few hours while permission is granted. It
          writes weight, body fat, nutrition, water and workouts back to Health Connect so other apps stay in sync.
        </Text>
        <Text size="sm" mt="xs">
          Data read from Health Connect is saved in the app and, when you&apos;re signed in, synced to your own
          account so your devices match.
        </Text>
        <Text size="sm" fw={600} mt="xs">
          Health Connect limited-use statement:
        </Text>
        <List size="sm">
          <List.Item>We use Health Connect data only to provide the features described in this policy.</List.Item>
          <List.Item>
            We share it only with our hosting provider (AWS), for storage and to sync your devices — never with
            advertisers, data brokers, resellers, or for credit, insurance, employment or lending decisions.
          </List.Item>
          <List.Item>
            No one at Macrotide reads it except with your permission, to keep the service secure, or where the law
            requires it.
          </List.Item>
          <List.Item>It&apos;s encrypted in transit and at rest.</List.Item>
        </List>
      </Section>

      <Section title="AI features" card={false}>
        <Text size="sm">
          AI features send only what each feature needs, through OpenRouter, which routes requests to AI model
          providers (currently OpenAI and Anthropic models; we may add Google&apos;s Gemini models):
        </Text>
        <List size="sm">
          <List.Item>
            <b>Photo or text logging:</b> the photo or text you submit.
          </List.Item>
          <List.Item>
            <b>Food logging:</b> your local time, your country, and the names of foods you log often (to improve
            matching and suggestions).
          </List.Item>
          <List.Item>
            <b>Matching custom foods, recipes or exercises:</b> the item you&apos;re matching.
          </List.Item>
          <List.Item>
            <b>Program changes:</b> your training program and training settings.
          </List.Item>
          <List.Item>
            <b>Explain (weekly review):</b> the weekly review&apos;s calorie figures. It does not receive your weight.
          </List.Item>
        </List>
        <Text size="sm" mt="xs">
          <b>Never sent to AI:</b> your name, email, account id, birth date, sex, height, ethnic background, body
          measurements, or any other Health Connect data.
        </Text>
        <Text size="sm" mt="xs">
          Every AI request sets <code>data_collection: deny</code>, so providers don&apos;t use your data to train
          their models; we&apos;ve deliberately avoided models that would require turning that setting on.
        </Text>
        <Text size="sm" mt="xs">
          What AI features give back is generated by AI: estimates and suggestions, marked as AI results in the app,
          that you check before saving. In the same request, the AI also checks whether what you sent is prohibited
          content (see &quot;Content checks and holds&quot;).
        </Text>
      </Section>

      <Section title="Content checks and holds" card={false}>
        <Text size="sm">
          If the AI flags a request as prohibited content (see the Terms, &quot;Prohibited content&quot;):
        </Text>
        <List size="sm">
          <List.Item>
            Your AI features are paused while a person reviews it (&quot;AI is paused while we review a recent
            request&quot;); everything else keeps working. For suspected child sexual abuse material, the account is
            suspended straight away.
          </List.Item>
          <List.Item>
            The request&apos;s photos and text, your account id, your full email address, the time and the flag are
            copied to a separate, restricted store as evidence. This copy stays even if you delete your account, for
            as long as the review or a report to the authorities is open.
          </List.Item>
          <List.Item>
            Staff with a moderation role review it, and every view is logged. Suspected child sexual abuse material
            is never shown to staff.
          </List.Item>
          <List.Item>
            If the flag was a mistake, we lift the hold and delete the evidence straight away. If we suspend or ban
            the account without reporting it, we delete the evidence 30 days after the decision. If we report it, we
            keep it until the authorities tell us we can delete it.
          </List.Item>
          <List.Item>
            Content about self-harm or suicide is never held or punished: the app shows where to find support (for
            example Samaritans, 116 123 in the UK), and nothing is kept as evidence.
          </List.Item>
        </List>
        <Text size="sm" mt="xs">
          We report child sexual abuse material to the Internet Watch Foundation and the National Crime Agency
          (including CEOP), information about terrorism to Counter Terrorism Policing (a legal duty under the
          Terrorism Act 2000), and threats to someone&apos;s life or safety to the police (for EU users, as the
          Digital Services Act requires). Otherwise we share information with law enforcement only where the law
          requires it or to protect someone&apos;s life. We may not tell you about a report if that could harm an
          investigation.
        </Text>
      </Section>

      <Section title="Notifications and in-app messages" card={false}>
        <Text size="sm">
          Notifications are off until you turn them on, on the phone (Android asks for permission) or in your
          browser. There are three kinds: <b>announcements</b> and <b>tips</b>, which you can switch off separately
          at any time, and <b>account</b> messages (security and changes to these policies), which stay on while
          notifications are on. In-app messages are shown inside the app and follow the same choices.
        </Text>
        <Text size="sm" mt="xs">
          To send them we keep your device&apos;s push token (or your browser&apos;s push subscription), the
          platform, app version, country, the kinds you&apos;ve chosen, and when the device was last seen. This
          works without an account too; if you&apos;re signed in, it&apos;s linked to your account. Phone
          notifications go through Google&apos;s Firebase Cloud Messaging (no Firebase Analytics); browser
          notifications go through your browser&apos;s push service (for example Google for Chrome, Mozilla for
          Firefox, Apple for Safari). We delete the token when you turn notifications off, sign out or delete your
          account, and drop any device not seen for 270 days.
        </Text>
      </Section>

      <Section title="Usage counts" card={false}>
        <Text size="sm">
          To run the service, control costs and stop abuse, our server counts how you use it: for each account and
          day, how many times each feature was called, your platform and app version, and what your AI requests
          cost. Guests are counted together, not one by one. A daily check flags accounts far above normal use for
          a person to look at; a flag doesn&apos;t block anything by itself. Apart from the ad code described under
          &quot;Ads&quot; (switched off for now), the apps contain no tracking code, and nothing is stored in your
          browser for this. Counts and flags are kept for 13 months.
        </Text>
      </Section>

      <Section title="Ads" card={false}>
        <Text size="sm">
          Macrotide is free. To pay for it, especially the AI, which costs us money every time you use it, the Android
          app can show ads from <b>Appodeal, Inc.</b> (575 Market St, San Francisco, USA) and the ad networks Appodeal
          works with. <b>Ads are switched off for now.</b> We&apos;ll email account holders before switching them on
          (see &quot;Changes&quot;). When ads are on:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            <b>Kinds of ads:</b> an occasional full-screen ad just after you finish something (for example a workout or
            the weekly review), never while you&apos;re logging, searching or using AI; and, only if you choose to watch
            one, a short &quot;rewarded&quot; ad that adds more AI requests for that day.
          </List.Item>
          <List.Item>
            <b>Non-personalised only:</b> ads aren&apos;t chosen from a profile of you or your interests. Nothing you log
            in Macrotide (food log, weight, measurements, photos, workouts, Health Connect data) is ever sent to Appodeal
            or any ad network, and Health Connect data is never used for ads.
          </List.Item>
          <List.Item>
            <b>What Appodeal and its ad networks receive from your phone:</b> your IP address, your device&apos;s
            advertising ID, basic device and app details (such as model, system version, language and app version), and
            which ads were shown or tapped. You can reset or delete the advertising ID any time in Android&apos;s
            settings (Privacy › Ads). Appodeal doesn&apos;t get your location from Macrotide: the app doesn&apos;t have
            location permission.
          </List.Item>
          <List.Item>
            <b>Consent:</b> in the UK, the EEA and Switzerland, the app asks for your consent before any ad is shown,
            using Google&apos;s consent form, and you can change your choice any time in the app&apos;s settings.
            Elsewhere, the app may show a privacy choice where the law asks for one.
          </List.Item>
          <List.Item>
            <b>Rewarded ads:</b> when you finish one, Appodeal tells our server your Macrotide account id (a random id,
            not your email), the reward, and a unique id for that viewing, so we can add the extra AI requests once. We
            keep this on that day&apos;s AI counter for about a day, and count rewarded ads in &quot;Usage counts&quot;.
          </List.Item>
        </List>
        <Text size="sm">
          Appodeal&apos;s own privacy policy covers what it and its partners do with this data:{' '}
          <Anchor href="https://appodeal.com/privacy-policy/" target="_blank" rel="noreferrer">
            appodeal.com/privacy-policy
          </Anchor>
          . The ad networks Appodeal may choose from are: Amazon Ads, AppLovin, Bigo Ads, BidMachine, Bidon, Digital
          Turbine (DT Exchange), InMobi, ironSource (Unity LevelPlay), Liftoff (Vungle), Mintegral, Unity Ads and VK
          Ads (myTarget). Appodeal and these networks decide for themselves how they use the data they collect for ads
          (each is a separate controller, under its own privacy policy), and may process it in the USA and elsewhere.
          Appodeal isn&apos;t certified under the EU–US Data Privacy Framework (or its UK extension) and doesn&apos;t
          publish standard contractual clauses for this; it relies on the consent you give, or refuse, in the ad
          consent form. <b>[Owner: ask Appodeal for a data processing agreement with the EU standard contractual
          clauses and the UK addendum (IDTA) before switching ads on; checked 3 Nov 2026, none is published.]</b>
        </Text>
      </Section>

      <Section title="Support emails and staff access" card={false}>
        <Text size="sm">
          Emails you send to macrotide@ikarosdev.com are hosted by Hostinger and copied to our AWS storage, where we
          keep them for 2 years, linked to your account by email address, so we can answer you and keep a record of
          requests and appeals. They&apos;re kept for that time even if you delete your account.
        </Text>
        <Text size="sm" mt="xs">
          People who work on Macrotide with a staff role (support or moderation) can see account details to handle
          requests: email, status, strikes, holds, restrictions, dates and usage counts. They don&apos;t see your
          food log, weights or photos, except content flagged for review or when you ask us to help with something
          specific. Every staff action, and every view of your content, is recorded in an audit log kept for 2
          years.
        </Text>
        <Text size="sm" mt="xs">
          Sign-in codes are emailed to you through Amazon SES (part of AWS).
        </Text>
      </Section>

      <Section title="Open Food Facts" card={false}>
        <Text size="sm">
          Barcode lookups while you scan are sent straight from your device to Open Food Facts&apos; public API
          (just the barcode number; no account or personal data travels with it).
        </Text>
        <Text size="sm" mt="xs">
          If you choose to send a product to Open Food Facts, Macrotide sends it for you from its own Open Food
          Facts account: the barcode, name, brand, nutrition and the photos you took, with an anonymous ID so Open
          Food Facts can moderate contributions. Your name, email and IP address aren&apos;t sent. Open Food Facts
          publishes products openly (ODbL for data, CC BY-SA for photos), so deleting your Macrotide account
          doesn&apos;t remove them.
        </Text>
      </Section>

      <Section title="Ethnic background" card={false}>
        <Text size="sm">
          Your ethnic background is optional, and under UK and EU data protection law it&apos;s &quot;special
          category data&quot;. We only ask for it, and only use it, to choose the right NICE BMI threshold for you.
          It&apos;s collected only with your explicit consent (&quot;By choosing an option you agree that Macrotide
          uses your ethnic background only to pick your BMI threshold. Clear it any time to withdraw&quot;), it&apos;s
          never sent to AI, and you can clear it at any time in Settings.
        </Text>
      </Section>

      <Section title="Consumer health data (Washington My Health My Data Act, Nevada SB 370)" card={false}>
        <List size="sm">
          <List.Item>
            <b>Categories:</b> weight, body measurements, food log, Health Connect data and progress photos.
          </List.Item>
          <List.Item>
            <b>Purpose:</b> to run the coaching and sync features described on this page.
          </List.Item>
          <List.Item>
            <b>Recipients:</b> AWS (hosting), OpenRouter and its AI providers (only what&apos;s listed under
            &quot;AI features&quot;, only when you use an AI feature), and the authorities only as described under
            &quot;Content checks and holds&quot;.
          </List.Item>
        </List>
        <Text size="sm" mt="xs">
          We only use sensitive personal information to provide the service: never sold, never used for advertising.
          To exercise your rights (access, deletion, or to withdraw consent), email macrotide@ikarosdev.com; we reply
          within a month.
        </Text>
      </Section>

      <Section title="Export" card={false}>
        <Text size="sm">
          On the web, signed in, download a ZIP with your data as CSV files and JSON, plus your photos (Settings ›
          Data &amp; integrations; the phone app has the same button under More › Account and sync › Export my
          data). You can make one new export a week. Download it from macrotide.ikarosdev.com/export/download for
          24 hours.
        </Text>
      </Section>

      <Section title="Automated decisions" card={false}>
        <Text size="sm">
          Two decisions are made automatically, without a person reviewing them first. Under UK and EU data
          protection law (Article 22 and, in the UK, Articles 22A to 22D) these are decisions made solely by
          automated means, and they can significantly affect you:
        </Text>
        <List size="sm">
          <List.Item>
            <b>Third strike:</b> ignoring repeated AI rate-limit warnings suspends your account on a third strike
            (Terms, &quot;Fair use&quot;). A suspended account can only export its data, and is scheduled for
            deletion in 30 days.
          </List.Item>
          <List.Item>
            <b>Content hold:</b> when the AI flags a request as prohibited content, your AI features are paused
            until a person reviews it (see &quot;Content checks and holds&quot;). Bans and reports to the authorities
            are always decided by a person.
          </List.Item>
        </List>
        <Text size="sm" mt="xs">
          We tell you straight away, in the app, what happened and why. You can explain your side, ask for a person
          to review it, and challenge the decision any time: email macrotide@ikarosdev.com. We review every case on
          request, and can lift it, clear your strikes, or let it stand.
        </Text>
      </Section>

      <Section title="Why we can use your data (lawful bases)" card={false}>
        <List size="sm">
          <List.Item>
            <b>Contract:</b> to provide the account, sync your devices, and run AI features you ask for.
          </List.Item>
          <List.Item>
            <b>Explicit consent:</b> for health data stored on our servers, and for ethnic background. Withdraw the
            first by deleting your account; clear the second any time in Settings.
          </List.Item>
          <List.Item>
            <b>Consent:</b> for announcement and tip notifications. Switch them off any time in Settings. And, when ads
            are on, for ads (UK and EU law requires consent before ad code stores or reads anything on your device);
            change your choice any time in the app&apos;s settings.
          </List.Item>
          <List.Item>
            <b>Legitimate interests:</b> for security, rate limiting, backups, AI reports, usage counts, content
            checks and holds, account notifications, the support inbox and the staff audit log — keeping the service
            working, affordable and safe. You can object to this processing at any time by emailing
            macrotide@ikarosdev.com.
          </List.Item>
          <List.Item>
            <b>Legal obligation and legal claims:</b> to keep evidence and report to the authorities where the law
            requires it (for health data in that evidence, to establish or defend legal claims and to prevent or
            detect unlawful acts).
          </List.Item>
          <List.Item>
            <b>Protecting someone&apos;s life:</b> to tell emergency services or the police when someone may be in
            danger.
          </List.Item>
        </List>
      </Section>

      <Section title="What we collect and how long we keep it" card={false}>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Data</Table.Th>
              <Table.Th>Retention</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {RETENTION.map(([data, retention]) => (
              <Table.Tr key={data}>
                <Table.Td>{data}</Table.Td>
                <Table.Td>{retention}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Section>

      <Section title="International transfers and recipients" card={false}>
        <Text size="sm">Your data is stored on AWS in eu-west-2 (London). We also share data with:</Text>
        <List size="sm">
          <List.Item>
            <b>AWS</b> (hosting, London) — storage, sync, and sending sign-in emails (Amazon SES).
          </List.Item>
          <List.Item>
            <b>OpenRouter, Inc. and the AI model providers it routes to</b> (USA) — only what&apos;s listed under
            &quot;AI features&quot;, only when you use an AI feature.
          </List.Item>
          <List.Item>
            <b>Google</b> (Firebase Cloud Messaging, USA) and <b>your browser&apos;s push service</b> — your push
            token and the notifications we send, only if you turn notifications on.
          </List.Item>
          <List.Item>
            <b>Hostinger</b> (email hosting) — emails you send to macrotide@ikarosdev.com.{' '}
            <b>[Owner to confirm the Hostinger mailbox region.]</b>
          </List.Item>
          <List.Item>
            <b>Open Food Facts</b> (France) — only when you send a product, as described above.
          </List.Item>
          <List.Item>
            <b>Appodeal, Inc. and the ad networks it works with</b> (USA and elsewhere) — only when ads are switched on,
            only what&apos;s listed under &quot;Ads&quot;.
          </List.Item>
          <List.Item>
            <b>Cloudflare</b> — DNS for our web addresses only; it doesn&apos;t see what you send to the app.
          </List.Item>
          <List.Item>
            <b>Google Play</b> — distributes the Android apps; Google&apos;s own privacy policy covers your Play
            account.
          </List.Item>
          <List.Item>
            <b>The Internet Watch Foundation, the National Crime Agency, the police or other authorities</b> — only
            as described under &quot;Content checks and holds&quot;.
          </List.Item>
        </List>
        <Text size="sm" mt="xs">
          Transfers outside the UK are made under the UK International Data Transfer Addendum (UK users) or the EU
          Standard Contractual Clauses (EU users). <b>[Owner to confirm OpenRouter DPA covers IDTA/SCCs.]</b>
        </Text>
      </Section>

      <Section title="Your rights" card={false}>
        <Text size="sm">
          Email macrotide@ikarosdev.com to access, correct, delete, restrict or export (port) your data, object to
          how we use it, or withdraw consent. We reply within a month. An email address is the only data we require
          to act on a request. See &quot;Automated decisions&quot; above for the decisions we make automatically and
          how to ask for a review.
        </Text>
      </Section>

      <Section title="Complaints" card={false}>
        <Text size="sm">
          If you&apos;re unhappy with how we handle your data, email macrotide@ikarosdev.com with &quot;Complaint&quot;
          in the subject. We&apos;ll confirm we&apos;ve received it within 30 days and reply without undue delay. You
          can also complain at any time to the UK Information Commission (ico.org.uk) or your local data protection
          authority.
        </Text>
      </Section>

      <Section title="California residents (CCPA/CPRA)" card={false}>
        <Text size="sm">
          We don&apos;t sell your personal information, and we don&apos;t share it for cross-context behavioural
          advertising: ads in Macrotide are non-personalised (see &quot;Ads&quot;).
        </Text>
      </Section>

      <Section title="Children" card={false}>
        <Text size="sm">
          Macrotide isn&apos;t intended for anyone under 18. Weight-loss goals are blocked for anyone under 18, and
          the app stores are set to an 18+ audience.
        </Text>
      </Section>

      <Section title="Security" card={false}>
        <Text size="sm">Data travels over HTTPS and is encrypted at rest.</Text>
      </Section>

      <Section title="Changes" card={false}>
        <Text size="sm">
          If we make a material change to this policy, we&apos;ll update the date at the top and email account
          holders before the change takes effect.
        </Text>
      </Section>

      <Section title="Deleting your account" card={false}>
        <Text size="sm">
          See <Anchor href="/delete-account">/delete-account</Anchor> for how.
        </Text>
      </Section>
    </LegalPage>
  )
}
